import { Color } from './java/Color';
import { Graphics2D } from './java/Graphics2D';
import { Rectangle } from './java/Geom';
import { GameObject } from './GameObject';
import { Handler } from './Handler';
import { ID } from './ID';

export class SpawnTelegraph extends GameObject {
  private static readonly DURATION = 60; // ~1 second at 60fps

  /** Java returned null from getBounds(); the TS abstract signature is non-null, so an
   *  empty (zero-area) rect is used — Rectangle.intersects() is false for it either way. */
  private static readonly EMPTY_BOUNDS = new Rectangle(0, 0, 0, 0);

  private handler: Handler;
  private toSpawn: GameObject;
  private timer = 0;
  private color: Color;
  private size: number;

  constructor(
    x: number,
    y: number,
    handler: Handler,
    toSpawn: GameObject,
    color: Color,
    size: number,
  ) {
    super(x, y, ID.SpawnTelegraph);
    this.handler = handler;
    this.toSpawn = toSpawn;
    this.color = color;
    this.size = size;
  }

  tick(): void {
    this.timer++;
    if (this.timer >= SpawnTelegraph.DURATION) {
      this.handler.removeObject(this);
      this.handler.addObject(this.toSpawn);
    }
  }

  render(g: Graphics2D): void {
    const g2 = g;
    const progress = this.timer / SpawnTelegraph.DURATION; // 0 to 1
    const cx = Math.trunc(this.x) + Math.trunc(this.size / 2);
    const cy = Math.trunc(this.y) + Math.trunc(this.size / 2);

    // Pulsing warning ring — expands then contracts
    const pulse = Math.sin(this.timer * 0.3) * 0.5 + 0.5;
    const ringRadius = Math.trunc(this.size * 0.8 + pulse * this.size * 0.4);
    const ringAlpha = Math.trunc((0.3 + pulse * 0.4) * 255 * (1 - progress * 0.3));
    g2.setColor(
      new Color(
        this.color.getRed(),
        this.color.getGreen(),
        this.color.getBlue(),
        Math.min(ringAlpha, 255),
      ),
    );
    g2.drawOval(
      cx - Math.trunc(ringRadius / 2),
      cy - Math.trunc(ringRadius / 2),
      ringRadius,
      ringRadius,
    );

    // Inner ring — tighter
    const innerRadius = Math.trunc(this.size * 0.4 + pulse * this.size * 0.2);
    const innerAlpha = Math.trunc(pulse * 120 * (1 - progress * 0.3));
    g2.setColor(
      new Color(
        this.color.getRed(),
        this.color.getGreen(),
        this.color.getBlue(),
        Math.min(innerAlpha, 255),
      ),
    );
    g2.drawOval(
      cx - Math.trunc(innerRadius / 2),
      cy - Math.trunc(innerRadius / 2),
      innerRadius,
      innerRadius,
    );

    // Materializing shape — fades in and scales up in the last 40%
    if (progress > 0.6) {
      const matProgress = (progress - 0.6) / 0.4; // 0 to 1
      const scale = 0.3 + matProgress * 0.7;
      const matAlpha = Math.trunc(matProgress * 180);
      const matSize = Math.trunc(this.size * scale);
      const mx = cx - Math.trunc(matSize / 2);
      const my = cy - Math.trunc(matSize / 2);
      g2.setColor(
        new Color(
          this.color.getRed(),
          this.color.getGreen(),
          this.color.getBlue(),
          Math.min(matAlpha, 255),
        ),
      );
      g2.fillRoundRect(mx, my, matSize, matSize, 8, 8);
    }

    // Cross-hair lines
    const crossAlpha = Math.trunc((1 - progress) * 60);
    g2.setColor(
      new Color(
        this.color.getRed(),
        this.color.getGreen(),
        this.color.getBlue(),
        Math.min(crossAlpha, 255),
      ),
    );
    const crossLen = Math.trunc(this.size * 1.2);
    g2.drawLine(cx - Math.trunc(crossLen / 2), cy, cx + Math.trunc(crossLen / 2), cy);
    g2.drawLine(cx, cy - Math.trunc(crossLen / 2), cx, cy + Math.trunc(crossLen / 2));
  }

  getBounds(): Rectangle {
    return SpawnTelegraph.EMPTY_BOUNDS; // doesn't collide
  }
}
